import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { AdvancedFilterFormState } from 'ducks/types/common-types';
import { default_filter_form_state } from 'modals/advanced-filtering/constants';
import { RefreshTimeArrayItem } from 'modals/constants';
import { checkNullableDate } from 'utils/modal/helpers';

export interface FilterContextType {
  form: AdvancedFilterFormState;
  setForm: Dispatch<SetStateAction<AdvancedFilterFormState>>;
  onChange: (item: Partial<AdvancedFilterFormState>) => void;
  onJobPagesSelect: (value: RefreshTimeArrayItem) => void;
  onRefreshClick: (value: boolean) => void;
  onDateChange: (date: 'start_date' | 'finish_date', value: string) => void;
  is_nullable_finish_date: boolean;
  is_nullable_start_date: boolean;
}

export interface FilterContextProviderProps {
  children: React.ReactNode;
}

export const FilterContext = createContext<FilterContextType>({} as FilterContextType);

export function useFilterContext() {
  return useContext(FilterContext);
}

export const FilterContextProvider = ({ children }: FilterContextProviderProps) => {
  const [form, setForm] = useState<AdvancedFilterFormState>({ ...default_filter_form_state });

  const is_nullable_finish_date = useMemo(() => checkNullableDate(form.finish_date), [form.finish_date]);
  const is_nullable_start_date = useMemo(() => checkNullableDate(form.start_date), [form.start_date]);

  const onChange = useCallback((item: Partial<AdvancedFilterFormState>) => {
    setForm((form) => ({ ...form, ...item }));
  }, []);

  const onJobPagesSelect = useCallback(
    (value: RefreshTimeArrayItem) => {
      onChange({
        refresh_timeout: value.name,
        min_refresh: value.time,
      });
    },
    [onChange],
  );

  const onRefreshClick = useCallback(
    (value: boolean) => {
      onChange({
        refresh_timeout: value ? '10m' : null,
        min_refresh: value ? 600000 : null,
      });
    },
    [onChange],
  );

  const onDateChange = useCallback(
    (date: 'start_date' | 'finish_date', value: string) => {
      onChange({
        range_name: null,
        [date]: value,
      });
    },
    [onChange],
  );

  const value = useMemo(
    () => ({
      form,
      setForm,
      onChange,
      onJobPagesSelect,
      onRefreshClick,
      onDateChange,
      is_nullable_finish_date,
      is_nullable_start_date,
    }),
    [form, onChange, onJobPagesSelect, onRefreshClick, onDateChange, is_nullable_finish_date, is_nullable_start_date],
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
